import React, { useState, useEffect } from 'react';
import YamlEditor from './YamlEditor';
import { fetchRulesets, fetchYamlCode } from '../services/backendApiService';
import { ProcessedRuleset } from '../types';
import { TokenService } from '../services/tokenService';

const RulesetAudit: React.FC = () => {
  const [rulesets, setRulesets] = useState<ProcessedRuleset[]>([]);
  const [selectedRuleset, setSelectedRuleset] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [availableVersions, setAvailableVersions] = useState<number[]>([]);
  const [yamlCode, setYamlCode] = useState('');
  const [error, setError] = useState('');
  const [yamlError, setYamlError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingYaml] = useState(false);
  const [rulesetSearchTerm, setRulesetSearchTerm] = useState('');
  const [showRulesetDropdown, setShowRulesetDropdown] = useState(false);
  const [guidelineReportLoading, setGuidelineReportLoading] = useState(false);
  const [guidelineReportProgress, setGuidelineReportProgress] = useState<{ current: number; total: number } | null>(null);
  const [guidelineReportProgressMessage, setGuidelineReportProgressMessage] = useState('');

  // Auto-load rulesets on component mount
  useEffect(() => {
    if (TokenService.hasValidToken()) {
      console.log('🔄 Auto-running fetch rulesets with cached token');
      handleFetchRulesets();
    }
  }, []);

  // Listen for token invalidation and clear state
  useEffect(() => {
    const checkTokenValidity = () => {
      const currentToken = TokenService.getToken();
      if (!currentToken) {
        console.log('🔒 Token was invalidated, clearing app state');
        setRulesets([]);
        setSelectedRuleset('');
        setSelectedVersion('');
        setAvailableVersions([]);
        setYamlCode('');
        setError('');
        setYamlError('');
      }
    };
    const interval = setInterval(checkTokenValidity, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFetchRulesets = async () => {
    setIsLoading(true);
    setError('');

    try {
      const result = await fetchRulesets();
      setRulesets(result.rulesets);
      console.log('✅ Rulesets loaded successfully');
    } catch (err) {
      console.error('❌ Error fetching rulesets:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch rulesets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRulesetSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value;
    setRulesetSearchTerm(searchTerm);
    setSelectedRuleset('');
    setSelectedVersion('');
    setAvailableVersions([]);
    setYamlCode('');
    setShowRulesetDropdown(true);
  };

  const handleRulesetSelect = (rulesetName: string) => {
    setSelectedRuleset(rulesetName);
    setRulesetSearchTerm(rulesetName);
    setShowRulesetDropdown(false);
    setSelectedVersion('');
    setAvailableVersions([]);
    setYamlCode('');

    // Find all major versions for the selected ruleset
    const selectedRulesetVersions = rulesets
      .filter(r => r.ruleset === rulesetName)
      .map(r => r.majorVersion);
    setAvailableVersions(selectedRulesetVersions);

    // Load YAML for the latest version
    handleRulesetChange(rulesetName);
  };

  const handleRulesetChange = async (rulesetName: string) => {
    if (!rulesetName) return;

    // Find the selected ruleset data (latest version)
    const selectedRulesetData = rulesets.find(r => r.ruleset === rulesetName);
    if (!selectedRulesetData) return;

    try {
      const savedToken = TokenService.getToken();
      if (!savedToken) {
        setYamlError('No token available');
        return;
      }

      const yamlContent = await fetchYamlCode(selectedRulesetData.latestRulesetKey);
      setYamlCode(yamlContent);
      setYamlError('');
    } catch (err) {
      console.error('❌ Error fetching YAML:', err);
      setYamlError(err instanceof Error ? err.message : 'Failed to fetch YAML');
      setYamlCode('');
    }
  };

  const handleVersionChange = async (version: string) => {
    console.log('🔄 Version changed to:', version);
    console.log('📋 Available versions:', availableVersions);
    console.log('📋 Selected ruleset:', selectedRuleset);
    
    setSelectedVersion(version);
    
    if (!version || !selectedRuleset) {
      console.log('❌ No version or ruleset selected');
      return;
    }

    try {
      const savedToken = TokenService.getToken();
      if (!savedToken) {
        setYamlError('No token available');
        return;
      }

      // Find the specific ruleset data for the selected version
      const selectedRulesetData = rulesets.find(r => 
        r.ruleset === selectedRuleset && 
        r.majorVersion === parseInt(version)
      );
      
      console.log('🔍 Looking for ruleset:', selectedRuleset, 'version:', parseInt(version));
      console.log('🔍 Found ruleset data:', selectedRulesetData);
      
      if (!selectedRulesetData) {
        console.log('❌ Selected version not found in rulesets');
        setYamlError('Selected version not found');
        return;
      }

      console.log('📥 Loading YAML for ruleset key:', selectedRulesetData.latestRulesetKey);
      
      // Load YAML for the specific version
      const yamlContent = await fetchYamlCode(selectedRulesetData.latestRulesetKey);
      setYamlCode(yamlContent);
      setYamlError('');
      console.log('✅ YAML loaded successfully for version:', version);
    } catch (err) {
      console.error('❌ Error fetching YAML for version:', err);
      setYamlError(err instanceof Error ? err.message : 'Failed to fetch YAML for selected version');
      setYamlCode('');
    }
  };

  // Get unique rulesets for display
  const uniqueRulesets = Array.from(new Set(rulesets.map(r => r.ruleset)));
  
  // Filter rulesets based on search term
  const filteredRulesets = uniqueRulesets.filter(rulesetName =>
    rulesetName.toLowerCase().includes(rulesetSearchTerm.toLowerCase())
  );

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.ruleset-search-container')) {
        setShowRulesetDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="ruleset-audit">
      <div className="ruleset-audit-container">
        {/* Ruleset Selection */}
        <div className="ruleset-selection-card">
          <div className="selection-row">
            <div className="ruleset-field">
              <label htmlFor="ruleset-search">Search Ruleset:</label>
              <div className="ruleset-search-container">
                <input
                  id="ruleset-search"
                  type="text"
                  value={rulesetSearchTerm}
                  onChange={handleRulesetSearchChange}
                  placeholder="Type to search for a ruleset..."
                  className="ruleset-search-input"
                  disabled={isLoading}
                />
                {showRulesetDropdown && filteredRulesets.length > 0 && (
                  <div className="ruleset-dropdown">
                    {filteredRulesets.map((rulesetName) => (
                      <div
                        key={rulesetName}
                        className="ruleset-option"
                        onClick={() => handleRulesetSelect(rulesetName)}
                      >
                        {rulesetName}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="version-field">
              <label htmlFor="version-select">Select Version:</label>
              <select
                id="version-select"
                value={selectedVersion}
                onChange={(e) => handleVersionChange(e.target.value)}
                className="ruleset-dropdown"
                disabled={!selectedRuleset || availableVersions.length === 0}
              >
                <option value="">Choose a version...</option>
                {availableVersions.map((version) => (
                  <option key={version} value={version.toString()}>
                    Version {version}.x
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedRuleset && selectedVersion && (
            <YamlEditor
              yamlCode={yamlCode}
              yamlLoading={isLoadingYaml}
              yamlError={yamlError}
              selectedRuleset={selectedRuleset}
              selectedRulesetKey={rulesets.find(r => r.ruleset === selectedRuleset)?.latestRulesetKey || ''}
              token={TokenService.getToken() || ''}
              guidelineReportLoading={guidelineReportLoading}
              guidelineReportProgress={guidelineReportProgress}
              guidelineReportProgressMessage={guidelineReportProgressMessage}
              setYamlError={setYamlError}
              setGuidelineReportLoading={setGuidelineReportLoading}
              setGuidelineReportProgress={setGuidelineReportProgress}
              setGuidelineReportProgressMessage={setGuidelineReportProgressMessage}
            />
          )}
        </div>

        {/* Loading and Error States */}
        {isLoading && (
          <div className="loading">
            🔄 Loading rulesets...
          </div>
        )}

        {error && (
          <div className="error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default RulesetAudit; 